import React, { useState, useEffect } from 'react';
import { useSpecializedVoucherPermissions } from '../../../hooks/useSpecializedVoucherPermissions';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  alpha,
  useTheme,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Grid,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../services/apiService';
import { checkSpecializedVouchersHaveTransactions } from '../../../services/financeService';
import { printSpecializedVoucher } from '../../../utils/printVoucher';

// واجهة قالب السند المتخصص
interface SpecializedVoucherTemplate {
  _id: string;
  id: string;
  name: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  description: string;
  isActive: boolean;
  requiresApproval: boolean;
  hasFees: boolean;
  debitAccounts: string[];  // معرفات الحسابات المدينة المتاحة
  creditAccounts: string[]; // معرفات الحسابات الدائنة المتاحة
  feeGroups: string[];      // معرفات مجموعات الرسوم المتاحة
  vouchersCount: number;    // عدد السندات المنشأة باستخدام هذا القالب
  createdAt: string;
  updatedAt: string;
  branch: string;
}

// واجهة السند المتخصص
interface SpecializedVoucher {
  id: string;
  templateId: string;
  templateName: string;
  voucherNumber: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  voucherDate: string;
  amount: number;
  debitAccountId: string;
  debitAccountName: string;
  creditAccountId: string;
  creditAccountName: string;
  operationNumber?: string;
  notes?: string;
  isApproved: boolean;
  appliedFeeGroupId?: string;
  appliedFeeGroupName?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

// مكون قائمة السندات المتخصصة
const SpecializedVouchersList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // استخدام hook للتحقق من صلاحيات السندات المتخصصة
  const { hasVoucherSettingPermission, hasSpecializedVoucherPermission } = useSpecializedVoucherPermissions();
  
  // استخدام معلمات الرابط للتحقق من وجود معلمة قالب محدد - سيتم تحديثها في useEffect
  const [isTemplateView, setIsTemplateView] = useState<boolean>(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  
  // التحقق من صلاحيات قوالب السندات المتخصصة
  const canCreateTemplate = hasVoucherSettingPermission('specialized-voucher-templates', 'create_template');
  const canViewTemplateDetails = hasVoucherSettingPermission('specialized-voucher-templates', 'view_template');
  const canEditTemplate = hasVoucherSettingPermission('specialized-voucher-templates', 'edit_template');
  const canDeleteTemplate = hasVoucherSettingPermission('specialized-voucher-templates', 'delete_template');
  
  // التحقق من صلاحيات السندات المتخصصة نفسها
  const [canCreateVoucher, setCanCreateVoucher] = useState(false);
  const [canViewVoucher, setCanViewVoucher] = useState(false);
  const [canEditVoucher, setCanEditVoucher] = useState(false);
  const [canDeleteVoucher, setCanDeleteVoucher] = useState(false);
  const [canChangeVoucherStatus, setCanChangeVoucherStatus] = useState(false);
  // التبويبات والبيانات الأساسية
  const [templates, setTemplates] = useState<SpecializedVoucherTemplate[]>([]);
  const [vouchers, setVouchers] = useState<SpecializedVoucher[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SpecializedVoucherTemplate | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [viewTemplateOpen, setViewTemplateOpen] = useState(false);
  
  // حالة التصفية
  const [filterOpen, setFilterOpen] = useState(false);
  const [voucherTypeFilter, setVoucherTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [operationNumberFilter, setOperationNumberFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  
  // حالة التقسيم إلى صفحات
  const [templatePage, setTemplatePage] = useState(1);
  const [voucherPage, setVoucherPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [totalVouchers, setTotalVouchers] = useState(0);
  const [tabValue, setTabValue] = useState<number>(0);
  
  // قائمة منسدلة للإجراءات
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<SpecializedVoucher | null>(null);
  const [confirmDeleteVoucherOpen, setConfirmDeleteVoucherOpen] = useState(false);

  // حالة المعاملات المالية للسندات المتخصصة
  const [voucherTransactions, setVoucherTransactions] = useState<Record<string, any>>({});
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // تحليل المسار لتحديد ما إذا كنا في عرض قالب محدد
  useEffect(() => {
    // تحليل المسار بشكل دقيق للكشف عن نمط /template/{ID}
    const pathSegments = location.pathname.split('/');
    const templateIndex = pathSegments.indexOf('template');
    
    // تحديد ما إذا كان المسار يحتوي على نمط /template/{ID}
    const newIsTemplateView = templateIndex !== -1 && templateIndex < pathSegments.length - 1;
    let newTemplateId: string | null = null;
    
    if (newIsTemplateView) {
      // استخراج معرف القالب من المسار - سيكون الجزء التالي بعد "template" في المسار
      newTemplateId = pathSegments[templateIndex + 1];
    }
    
    // تحديث حالة العرض ومعرف القالب
    setIsTemplateView(newIsTemplateView);
    setTemplateId(newTemplateId);
    
    // معالجة رسائل النجاح من معلمات الاستعلام
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    
    if (success === 'true') {
      setSuccessMessage('تم حفظ قالب السند المتخصص بنجاح');
      
      params.delete('success');
      navigate({
        pathname: location.pathname,
        search: params.toString()
      }, { replace: true });
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    }
  }, [location, navigate]);
  
  // تحديث صلاحيات السندات المتخصصة عند تغير معرف القالب
  useEffect(() => {
    setCanCreateVoucher(hasSpecializedVoucherPermission(templateId, 'create'));
    setCanViewVoucher(hasSpecializedVoucherPermission(templateId, 'view'));
    setCanEditVoucher(hasSpecializedVoucherPermission(templateId, 'edit'));
    setCanDeleteVoucher(hasSpecializedVoucherPermission(templateId, 'delete'));
    setCanChangeVoucherStatus(hasSpecializedVoucherPermission(templateId, 'change_status'));
  }, [templateId, hasSpecializedVoucherPermission]);

  // تحميل البيانات المناسبة استنادًا إلى نوع العرض
  useEffect(() => {
    // إعادة تعيين حالة التحميل في كل مرة نحتاج لتحميل بيانات جديدة
    setLoading(true);
    setError(null);
    
    console.log(`حالة العرض: isTemplateView=${isTemplateView}, templateId=${templateId}, tabValue=${tabValue}`);
    
    // إذا كنا في صفحة عرض سندات قالب محدد - نعرض فقط السندات
    if (isTemplateView && templateId) {
      console.log(`جاري تحميل عرض سندات القالب: ${templateId}`);
      setTabValue(1); // تأكيد على أننا في تبويب السندات وليس القوالب
      
      // جلب معلومات القالب المحدد
      fetchSingleTemplate(templateId);
      
      // جلب السندات المرتبطة بهذا القالب فقط - نؤخر قليلاً للتأكد من تحميل القالب أولاً
      setTimeout(() => {
        fetchVouchers();
      }, 300); // زيادة التأخير لضمان تحميل القالب أولاً
    }
    // وإلا، نعتمد على المعلمات في الرابط - عرض طبيعي يتضمن قوالب السندات والسندات
    else {
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab');
      
      if (tab === 'vouchers') {
        setTabValue(1);
        fetchVouchers();
      } else {
        setTabValue(0);
        fetchTemplates();
      }
    }
  }, [isTemplateView, templateId, location.search, voucherPage, itemsPerPage, searchTerm, statusFilter, operationNumberFilter, dateFrom, dateTo]);

  // إعادة تعيين رقم الصفحة إلى 1 عند تغيير الفلاتر
  useEffect(() => {
    setVoucherPage(1);
  }, [searchTerm, statusFilter, operationNumberFilter, dateFrom, dateTo, voucherTypeFilter, templateId]);

  // جلب بيانات المعاملات المالية للسندات المتخصصة
  useEffect(() => {
    const fetchVoucherTransactions = async () => {
      if (vouchers.length === 0) return;

      // تأخير 600ms قبل جلب البيانات
      const timer = setTimeout(async () => {
        try {
          setTransactionsLoading(true);
          const voucherNumbers = vouchers.map(v => v.voucherNumber);
          const response = await checkSpecializedVouchersHaveTransactions(voucherNumbers);
          
          if (response.success) {
            setVoucherTransactions(response.results);
          }
        } catch (error) {
          console.error('Error fetching voucher transactions:', error);
        } finally {
          setTransactionsLoading(false);
        }
      }, 600);

      return () => clearTimeout(timer);
    };

    fetchVoucherTransactions();
  }, [vouchers]);

  // معالجة البيانات القادمة من الخادم
  const processTemplateData = (template: any): SpecializedVoucherTemplate => {
    // تأكد من أن لدينا معرف صالح - استخدم _id من MongoDB أولاً ثم id إذا كان موجودًا
    const validId = template._id || template.id || '';
    
    return {
      id: validId,
      _id: validId,
      name: template.name || '',
      voucherType: template.voucherType || 'receipt',
      description: template.description || '',
      isActive: typeof template.isActive === 'boolean' ? template.isActive : true,
      requiresApproval: typeof template.requiresApproval === 'boolean' ? template.requiresApproval : false,
      hasFees: typeof template.hasFees === 'boolean' ? template.hasFees : false,
      debitAccounts: Array.isArray(template.debitAccounts) ? template.debitAccounts : [],
      creditAccounts: Array.isArray(template.creditAccounts) ? template.creditAccounts : [],
      feeGroups: Array.isArray(template.feeGroups) ? template.feeGroups : [],
      vouchersCount: typeof template.vouchersCount === 'number' ? template.vouchersCount : 0,
      createdAt: template.createdAt || '',
      updatedAt: template.updatedAt || '',
      branch: template.branch || ''
    };
  };

  // تحميل قوالب السندات المتخصصة
  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // استخدام معلمات الاستعلام للتأكد من استرجاع البيانات حتى في حالة عدم وجود بيانات مستخدم في التخزين المحلي
      const response = await apiService.get<any[]>('/finance/specialized-vouchers/templates');
      console.log('تم استرجاع قوالب السندات:', response);
      
      if (!response || !Array.isArray(response)) {
        console.error('تنسيق غير متوقع للبيانات المسترجعة:', response);
        setTemplates([]);
        setError('تنسيق غير صالح للبيانات المسترجعة من الخادم');
        setLoading(false);
        return;
      }
      
      // معالجة البيانات للتأكد من مطابقتها للواجهة المتوقعة
      let processedTemplates = response.map(template => processTemplateData(template));
      
      // استرداد عدد السندات لكل قالب إذا لم تكن موجودة في البيانات الأصلية
      // استخدام طلب مجمع للحصول على عدد السندات لكل القوالب
      try {
        // استخدام طريقة بديلة للحصول على عدد السندات بدون استخدام مسار /counts
        const vouchersCountsResponse = await apiService.get('/finance/specialized-vouchers/statistics');
        console.log('تم استرجاع إحصائيات السندات لكل قالب:', vouchersCountsResponse);
        
        if (vouchersCountsResponse && typeof vouchersCountsResponse === 'object') {
          // تحديث عدد السندات لكل قالب إذا كانت البيانات متوفرة
          processedTemplates = processedTemplates.map(template => {
            const templateId = template.id || template._id;
            const count = vouchersCountsResponse[templateId] ?? 0;
            return {
              ...template,
              vouchersCount: count
            };
          });
        }
      } catch (countError) {
        console.error('خطأ في استرجاع عدد السندات للقوالب:', countError);
        // متابعة العمل حتى مع وجود خطأ في استرجاع العدد
      }
      
      console.log('تمت معالجة القوالب:', processedTemplates);
      
      setTemplates(processedTemplates);
      setLoading(false);
    } catch (err: any) {
      console.error('خطأ في تحميل قوالب السندات المتخصصة:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات قوالب السندات المتخصصة');
      setTemplates([]);
      setLoading(false);
    }
  };

  // جلب قالب سند محدد بالمعرف
  const fetchSingleTemplate = async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get<any>(`/finance/specialized-vouchers/templates/${id}`);
      console.log('تم استرجاع تفاصيل القالب:', response);
      
      if (!response) {
        console.error('لم يتم العثور على قالب السند:', id);
        setError('لم يتم العثور على قالب السند المطلوب');
        setLoading(false);
        return;
      }
      
      // معالجة البيانات للتأكد من مطابقتها للواجهة المتوقعة
      const processedTemplate = processTemplateData(response);
      setSelectedTemplate(processedTemplate);
      
      // عرض عنوان القالب في واجهة المستخدم
      setSuccessMessage(`قالب السند: ${processedTemplate.name}`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (err: any) {
      console.error('خطأ في تحميل بيانات قالب السند:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات قالب السند المحدد');
    } finally {
      setLoading(false);
    }
  };

  // تحميل السندات المتخصصة
  const fetchVouchers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // إعادة تعيين حالة الخطأ عند كل محاولة تحميل جديدة
      setVouchers([]); // إفراغ قائمة السندات السابقة لتجنب عرض بيانات قديمة
      
      // بناء رابط الاستعلام مع إضافة معلمات الصفحة
      let apiUrl = `/finance/specialized-vouchers?page=${voucherPage}&limit=${itemsPerPage}`;
      
      // إضافة البحث إذا كان موجوداً
      if (searchTerm) {
        apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // إضافة فلتر نوع السند
      if (voucherTypeFilter && voucherTypeFilter !== 'all') {
        // سنحتاج لتصفية هذا في Frontend لأن Backend يحتاج template
      }
      
      // إضافة فلتر الحالة
      if (statusFilter && statusFilter !== 'all') {
        const isApproved = statusFilter === 'active';
        apiUrl += `&approved=${isApproved}`;
      }
      
      // إضافة فلتر رقم العملية في البحث
      if (operationNumberFilter) {
        apiUrl += `&search=${encodeURIComponent(operationNumberFilter)}`;
      }
      
      // إضافة فلتر التاريخ من
      if (dateFrom) {
        apiUrl += `&dateFrom=${encodeURIComponent(dateFrom)}`;
      }
      
      // إضافة فلتر التاريخ إلى
      if (dateTo) {
        apiUrl += `&dateTo=${encodeURIComponent(dateTo)}`;
      }
      
      // إذا كنا في عرض سندات قالب محدد، نضيف معرف القالب للتصفية
      if (isTemplateView && templateId) {
        console.log(`تصفية السندات حسب قالب محدد: ${templateId}`);
        apiUrl += `&template=${templateId}`;
      }
      
      console.log('جاري تحميل السندات باستخدام الرابط:', apiUrl);
      
      const response = await apiService.get<{
        vouchers: any[],
        total: number
      }>(apiUrl);
      
      console.log('استجابة تحميل السندات:', response);
      
      // تعيين العدد الإجمالي للسندات للتقسيم إلى صفحات
      if (response && typeof response === 'object') {
        const totalCount = response.total || 0;
        setTotalVouchers(totalCount);
        
        // التأكد من أن السندات دائمًا مصفوفة، حتى إذا كانت بنية استجابة API غير متوقعة
        let vouchersData: SpecializedVoucher[] = [];
        
        if (Array.isArray(response.vouchers)) {
          vouchersData = response.vouchers;
        } else if (response.vouchers && typeof response.vouchers === 'object') {
          // إذا كان هناك كائن يحتوي على السندات بدلاً من مصفوفة
          vouchersData = Object.values(response.vouchers);
        } else if (Array.isArray(response)) {
          // إذا كانت الاستجابة مصفوفة بكاملها
          vouchersData = response;
        }
        
        console.log(`تم تحميل ${vouchersData.length} من السندات`);
        setVouchers(vouchersData);
        
        // إذا كنا في عرض سندات قالب محدد وليس لدينا سندات، نعرض رسالة
        if (isTemplateView && templateId && vouchersData.length === 0) {
          setSuccessMessage('لا توجد سندات مرتبطة بهذا القالب بعد');
          setTimeout(() => {
            setSuccessMessage(null);
          }, 5000);
        }
      } else {
        console.error('استجابة API غير صالحة:', response);
        setError('تم استلام بيانات غير صالحة من الخادم');
        setVouchers([]);
      }
    } catch (err: any) {
      console.error('خطأ في تحميل السندات المتخصصة:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات السندات المتخصصة');
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  // حذف قالب سند متخصص
  const handleDeleteTemplate = async () => {
    if (selectedTemplate) {
      setLoading(true);
      
      try {
        await apiService.delete(`/finance/specialized-vouchers/templates/${selectedTemplate.id}`);
        
        // إغلاق مربع الحوار وتحديث القائمة
        setConfirmDeleteOpen(false);
        setSelectedTemplate(null);
        fetchTemplates();
      } catch (err: any) {
        console.error('خطأ في حذف قالب السند المتخصص:', err);
        setError(err.response?.data?.message || 'حدث خطأ في حذف قالب السند المتخصص');
        setLoading(false);
        setConfirmDeleteOpen(false);
      }
    }
  };

  // تغيير علامة التبويب
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // تحديث الرابط
    const params = new URLSearchParams(location.search);
    if (newValue === 1) {
      params.set('tab', 'vouchers');
      fetchVouchers();
    } else {
      params.delete('tab');
      fetchTemplates();
    }
    
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
  };

  // تعديل قالب سند متخصص
  const handleEditTemplate = (id: string) => {
    navigate(`/finance/specialized-vouchers/templates/edit/${id}`);
  };

  // إنشاء قالب سند متخصص جديد
  const handleCreateTemplate = () => {
    navigate('/finance/specialized-vouchers/templates/new');
  };

  // عرض تفاصيل القالب
  const handleViewTemplate = (template: SpecializedVoucherTemplate) => {
    setSelectedTemplate(template);
    setViewTemplateOpen(true);
  };

  // إنشاء سند جديد باستخدام قالب
  const handleCreateVoucher = (templateId?: string) => {
    // تحديد مسار العودة (المسار الحالي) لاستخدامه عند الرجوع من صفحة إنشاء السند
    const returnPath = location.pathname + location.search;
    
    // إذا تم تمرير معرف قالب، ننتقل مباشرة إلى صفحة إنشاء سند باستخدام هذا القالب
    if (templateId) {
      navigate(`/finance/specialized-vouchers/new/${templateId}?returnPath=${encodeURIComponent(returnPath)}`);
    } else {
      // وإلا ننتقل إلى صفحة اختيار القالب لإنشاء سند جديد
      navigate(`/finance/specialized-vouchers/new?returnPath=${encodeURIComponent(returnPath)}`);
    }
  };

  // عرض سندات قالب معين
  const handleViewTemplateVouchers = (templateId: string) => {
    console.log(`تم النقر على زر عرض سندات القالب: ${templateId}`);
    
    // إعادة تعيين حالة القائمة قبل الانتقال للتأكد من عدم ترحيل حالة قديمة
    setSearchTerm('');
    setVoucherTypeFilter('all');
    setStatusFilter('all');
    setOperationNumberFilter('');
    setVoucherPage(1);
    
    // تأخير الانتقال قليلاً لتجنب مشاكل التحديث المتزامن
    setTimeout(() => {
      navigate(`/finance/specialized-vouchers/template/${templateId}`);
    }, 50);
  };

  // فتح قائمة إجراءات السند
  const handleVoucherMenuOpen = (event: React.MouseEvent<HTMLElement>, voucherId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedVoucherId(voucherId);
    // تخزين السند المحدد للاستخدام في حوار تأكيد الحذف
    const selectedVoucher = vouchers.find(v => v.id === voucherId) || null;
    setSelectedVoucher(selectedVoucher);
  };

  // إغلاق قائمة إجراءات السند
  const handleVoucherMenuClose = () => {
    setAnchorEl(null);
    setSelectedVoucherId(null);
  };

  // عرض تفاصيل السند
  const handleViewVoucher = (id: string) => {
    handleVoucherMenuClose();
    const returnPath = location.pathname + location.search;
    navigate(`/finance/specialized-vouchers/${id}?returnPath=${encodeURIComponent(returnPath)}`);
  };

  // تعديل سند
  const handleEditVoucher = (id: string) => {
    handleVoucherMenuClose();
    const returnPath = location.pathname + location.search;
    navigate(`/finance/specialized-vouchers/edit/${id}?returnPath=${encodeURIComponent(returnPath)}`);
  };

  // طباعة السند
  const handlePrintVoucher = async (id: string) => {
    handleVoucherMenuClose();
    try {
      await printSpecializedVoucher(id);
    } catch (error) {
      console.error('خطأ في طباعة السند:', error);
      setError('حدث خطأ أثناء طباعة السند');
      setTimeout(() => setError(null), 3000);
    }
  };

  // تغيير حالة السند (معتمد/قيد الانتظار)
  const handleToggleVoucherStatus = async (id: string) => {
    handleVoucherMenuClose();
    
    // العثور على السند الحالي لمعرفة حالته الحالية
    const voucher = vouchers.find(v => v.id === id);
    if (!voucher) return;
    
    try {
      setLoading(true);
      
      // إنشاء الطلب - تغيير الحالة إلى عكس الحالة الحالية
      const response = await apiService.put(`/finance/specialized-vouchers/${id}`, {
        isApproved: !voucher.isApproved
      });
      
      // عرض رسالة نجاح
      setSuccessMessage(`تم تغيير حالة السند "${voucher.voucherNumber}" بنجاح`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // إعادة تحميل البيانات
      fetchVouchers();
    } catch (error: any) {
      console.error('خطأ في تغيير حالة السند:', error);
      setError(error.response?.data?.message || 'حدث خطأ في تغيير حالة السند');
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };
  
  // حذف سند متخصص
  const handleDeleteVoucher = async (id: string) => {
    // لا نغلق القائمة هنا لأن السند المختار قد يكون null
    
    try {
      setLoading(true);
      console.log(`بدء عملية حذف السند برقم معرف: ${id}`);
      
      // طلب حذف السند من الخادم
      const response = await apiService.delete<{ message: string }>(`/finance/specialized-vouchers/${id}`);
      
      console.log('استجابة الحذف:', response);
      
      // عرض رسالة نجاح
      setSuccessMessage('تم حذف السند المتخصص بنجاح');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // إعادة تحميل البيانات
      fetchVouchers();
    } catch (error: any) {
      console.error('خطأ في حذف السند المتخصص:', error);
      setError(error.response?.data?.message || 'حدث خطأ في حذف السند المتخصص');
      console.log('تفاصيل الخطأ:', error);
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setLoading(false);
      setConfirmDeleteVoucherOpen(false);
      setSelectedVoucher(null);
    }
  };

  // تصفية قوالب السندات حسب البحث والفلاتر
  const filteredTemplates = templates.filter(template => {
    // التصفية حسب نص البحث
    const searchMatch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // التصفية حسب نوع السند
    const typeMatch = voucherTypeFilter === 'all' || template.voucherType === voucherTypeFilter;
    
    // التصفية حسب الحالة
    const statusMatch = statusFilter === 'all' ||
      (statusFilter === 'active' && template.isActive) ||
      (statusFilter === 'inactive' && !template.isActive);
    
    return searchMatch && typeMatch && statusMatch;
  });

  // السندات تأتي من Backend مع التصفية، لا نحتاج لتصفية Frontend
  // نحتفظ فقط بالتصفية حسب نوع السند لأنها تحتاج template في Backend
  const filteredVouchers = vouchers.filter(voucher => {
    // التصفية حسب نوع السند (Frontend فقط)
    const typeMatch = voucherTypeFilter === 'all' || voucher.voucherType === voucherTypeFilter;
    return typeMatch;
  });
  
  // التعامل مع تغيير الصفحة
  const handleTemplatePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setTemplatePage(value);
  };
  
  const handleVoucherPageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setVoucherPage(value);
  };
  
  // التعامل مع تغيير حجم الصفحة
  const handleItemsPerPageChange = (event: SelectChangeEvent) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setTemplatePage(1); // إعادة تعيين الصفحة عند تغيير حجم الصفحة
    setVoucherPage(1);
  };
  
  // التعامل مع تغيير الفلتر
  const handleVoucherTypeFilterChange = (event: SelectChangeEvent) => {
    setVoucherTypeFilter(event.target.value);
    setVoucherPage(1); // إعادة تعيين الصفحة عند تغيير الفلتر
  };
  
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setVoucherPage(1); // إعادة تعيين الصفحة عند تغيير الفلتر
  };
  
  // حساب العناصر التي سيتم عرضها في الصفحة الحالية
  const paginatedTemplates = filteredTemplates.slice(
    (templatePage - 1) * itemsPerPage,
    templatePage * itemsPerPage
  );
  
  // السندات تأتي من Backend مع pagination، نستخدمها مباشرة
  const paginatedVouchers = filteredVouchers;

  // عرض نوع السند بالعربية
  const getVoucherTypeText = (type: 'receipt' | 'payment' | 'journal') => {
    switch (type) {
      case 'receipt': return 'سند قبض';
      case 'payment': return 'سند صرف';
      case 'journal': return 'سند موازنة';
      default: return '';
    }
  };

  // تنسيق التاريخ
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة مع أدوات البحث والتحكم */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {isTemplateView && selectedTemplate
                ? `سندات قالب "${selectedTemplate.name}"`
                : tabValue === 0 ? "قوالب السندات المتخصصة" : "السندات المتخصصة"
              }
            </Typography>
            {isTemplateView && selectedTemplate && (
              <Typography variant="subtitle2" color="text.secondary">
                {getVoucherTypeText(selectedTemplate.voucherType)} - {selectedTemplate.description || "لا يوجد وصف"}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              placeholder="بحث..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ width: 250 }}
            />
            
            <Tooltip title="عرض الفلاتر" arrow>
              <IconButton
                onClick={() => setFilterOpen(!filterOpen)}
                color="primary"
                sx={{ bgcolor: filterOpen ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}
              >
                {filterOpen ? <ExpandLessIcon /> : <FilterListIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="تحديث" arrow>
              <IconButton
                onClick={tabValue === 0 ? fetchTemplates : fetchVouchers}
                color="primary"
                disabled={loading}
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            {/* زر إنشاء قالب سند جديد - يظهر فقط في تبويب القوالب وللمستخدمين الذين لديهم صلاحية create_template */}
            {tabValue === 0 && !isTemplateView && canCreateTemplate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateTemplate}
                sx={{ borderRadius: '8px', mr: 1 }}
              >
                إنشاء قالب سند جديد
              </Button>
            )}
            
            {/* زر إنشاء سند متخصص جديد - يظهر في تبويب السندات أو عند عرض سندات قالب محدد */}
            {(tabValue === 1 || isTemplateView) && canCreateVoucher && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ReceiptIcon />}
                onClick={() => {
                  // إذا كنا في عرض سندات قالب محدد، نستخدم معرف القالب المحدد
                  if (isTemplateView && templateId) {
                    handleCreateVoucher(templateId);
                  } else {
                    // وإلا ننتقل إلى صفحة اختيار القالب لإنشاء سند جديد
                    navigate('/finance/specialized-vouchers/new');
                  }
                }}
                sx={{ borderRadius: '8px' }}
              >
                إنشاء سند متخصص جديد
              </Button>
            )}
          </Box>
        </Box>
        
        {/* قسم الفلاتر */}
        <Collapse in={filterOpen}>
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="voucher-type-filter-label">نوع السند</InputLabel>
                  <Select
                    labelId="voucher-type-filter-label"
                    id="voucher-type-filter"
                    value={voucherTypeFilter}
                    label="نوع السند"
                    onChange={handleVoucherTypeFilterChange}
                  >
                    <MenuItem value="all">جميع الأنواع</MenuItem>
                    <MenuItem value="receipt">سند قبض</MenuItem>
                    <MenuItem value="payment">سند صرف</MenuItem>
                    <MenuItem value="journal">سند موازنة</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-filter-label">الحالة</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilter}
                    label="الحالة"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">جميع الحالات</MenuItem>
                    <MenuItem value="active">{tabValue === 0 ? 'مفعل' : 'معتمد'}</MenuItem>
                    <MenuItem value="inactive">{tabValue === 0 ? 'معطل' : 'قيد الانتظار'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="رقم العملية"
                  value={operationNumberFilter}
                  onChange={(e) => setOperationNumberFilter(e.target.value)}
                  placeholder="البحث برقم العملية..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="من تاريخ"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="إلى تاريخ"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="items-per-page-label">عدد العناصر في الصفحة</InputLabel>
                  <Select
                    labelId="items-per-page-label"
                    id="items-per-page"
                    value={itemsPerPage.toString()}
                    label="عدد العناصر في الصفحة"
                    onChange={handleItemsPerPageChange}
                  >
                    <MenuItem value="5">5</MenuItem>
                    <MenuItem value="10">10</MenuItem>
                    <MenuItem value="25">25</MenuItem>
                    <MenuItem value="50">50</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3} sx={{ textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setVoucherTypeFilter('all');
                    setStatusFilter('all');
                    setOperationNumberFilter('');
                    setSearchTerm('');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  إعادة تعيين الفلاتر
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
        
        {/* علامات التبويب - تظهر فقط إذا لم نكن في عرض سندات قالب محدد */}
        {!isTemplateView && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="specialized vouchers tabs"
            >
              <Tab label="قوالب السندات" />
              <Tab label="السندات" />
            </Tabs>
          </Box>
        )}
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* عرض رسالة النجاح */}
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}
        
        {/* عرض محتوى التبويب النشط */}
        <Box role="tabpanel" hidden={tabValue !== 0 || isTemplateView}>
          {tabValue === 0 && (
            <TableContainer sx={{ 
              maxHeight: 'calc(100vh - 280px)', 
              overflowY: 'auto',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 1
            }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>اسم القالب</TableCell>
                    <TableCell>نوع السند</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>يتطلب موافقة</TableCell>
                    <TableCell>تطبيق رسوم</TableCell>
                    <TableCell>عدد السندات</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
                      </TableCell>
                    </TableRow>
                  ) : filteredTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          لا توجد قوالب سندات متطابقة مع البحث
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTemplates.map((template) => (
                      <TableRow key={template._id || template.id} hover>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {template.name}
                          </Typography>
                          {template.description && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {template.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getVoucherTypeText(template.voucherType)}
                            variant="outlined"
                            size="small"
                            color={
                              template.voucherType === 'receipt' ? 'success' :
                              template.voucherType === 'payment' ? 'error' : 'primary'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.isActive ? 'مفعل' : 'معطل'}
                            variant="outlined"
                            size="small"
                            color={template.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {template.requiresApproval ? 'نعم' : 'لا'}
                        </TableCell>
                        <TableCell>
                          {template.hasFees ? 'نعم' : 'لا'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.vouchersCount.toString()}
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => handleViewTemplateVouchers(template.id)}
                            clickable
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* زر إنشاء سند - متاح للجميع */}
                            <Tooltip title="إنشاء سند" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleCreateVoucher(template.id)}
                                sx={{
                                  color: theme.palette.success.main,
                                  '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) }
                                }}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {/* زر عرض تفاصيل القالب - يظهر فقط للمستخدمين الذين لديهم صلاحية view_template */}
                            {canViewTemplateDetails && (
                              <Tooltip title="عرض التفاصيل" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewTemplate(template)}
                                  sx={{
                                    color: theme.palette.info.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* زر تعديل القالب - يظهر فقط للمستخدمين الذين لديهم صلاحية edit_template */}
                            {canEditTemplate && (
                              <Tooltip title="تعديل" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditTemplate(template.id)}
                                  sx={{
                                    color: theme.palette.primary.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* زر حذف القالب - يظهر فقط للمستخدمين الذين لديهم صلاحية delete_template */}
                            {canDeleteTemplate && (
                              <Tooltip title="حذف" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedTemplate(template);
                                    setConfirmDeleteOpen(true);
                                  }}
                                  sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                                  }}
                                  disabled={template.vouchersCount > 0}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* التنقل بين الصفحات - جدول القوالب */}
              {filteredTemplates.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}>
                  <Pagination
                    count={Math.ceil(filteredTemplates.length / itemsPerPage)}
                    page={templatePage}
                    onChange={handleTemplatePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{ direction: 'ltr' }}
                  />
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
        
        {/* محتوى تبويب السندات */}
        <Box role="tabpanel" hidden={tabValue !== 1 && !isTemplateView}>
          {tabValue === 1 && (
            <TableContainer sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 1
            }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>رقم السند</TableCell>
                    <TableCell>رقم العملية</TableCell>
                    <TableCell>نوع السند</TableCell>
                    <TableCell>القالب</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>من حساب</TableCell>
                    <TableCell>إلى حساب</TableCell>
                    <TableCell align="right">المبلغ</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>المعاملات</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
                      </TableCell>
                    </TableRow>
                  ) : filteredVouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          لا توجد سندات متطابقة مع البحث
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedVouchers.map((voucher) => (
                      <TableRow key={voucher.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {voucher.voucherNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={voucher.operationNumber ? 'text.primary' : 'text.secondary'}>
                            {voucher.operationNumber || 'غير محدد'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getVoucherTypeText(voucher.voucherType)}
                            variant="outlined"
                            size="small"
                            color={
                              voucher.voucherType === 'receipt' ? 'success' :
                              voucher.voucherType === 'payment' ? 'error' : 'primary'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{voucher.templateName}</Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(voucher.voucherDate)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{voucher.debitAccountName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{voucher.creditAccountName}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {voucher.amount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.isApproved ? 'معتمد' : 'قيد الانتظار'}
                            variant="outlined"
                            size="small"
                            color={voucher.isApproved ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {transactionsLoading ? (
                            <CircularProgress size={20} />
                          ) : !voucher.isApproved ? (
                            <Tooltip title="السند قيد الانتظار - لم يتم إنشاء معاملات مالية بعد" arrow>
                              <Chip
                                icon={<WarningIcon />}
                                label="قيد الانتظار"
                                size="small"
                                color="warning"
                              />
                            </Tooltip>
                          ) : voucherTransactions[voucher.voucherNumber] ? (
                            <Tooltip
                              title={
                                <div style={{ whiteSpace: 'pre-line' }}>
                                  {`حالة الاعتماد: ${voucherTransactions[voucher.voucherNumber].isApproved ? 'معتمد' : 'غير معتمد'}\n` +
                                   `عدد المعاملات: ${voucherTransactions[voucher.voucherNumber].transactionsCount}\n` +
                                   `مبلغ السند: ${voucherTransactions[voucher.voucherNumber].voucherAmount.toFixed(2)} ريال\n` +
                                   `مبلغ المدين: ${voucherTransactions[voucher.voucherNumber].debitAmount.toFixed(2)} ريال\n` +
                                   `مبلغ الدائن: ${voucherTransactions[voucher.voucherNumber].creditAmount.toFixed(2)} ريال\n` +
                                   `الحساب المدين: ${voucherTransactions[voucher.voucherNumber].debitAccount}\n` +
                                   `الحساب الدائن: ${voucherTransactions[voucher.voucherNumber].creditAccount}` +
                                   (voucherTransactions[voucher.voucherNumber].issues ? `\n\nالمشاكل:\n${voucherTransactions[voucher.voucherNumber].issues.map((i: string) => `• ${i}`).join('\n')}` : '')}
                                </div>
                              }
                              arrow
                            >
                              <Chip
                                icon={
                                  voucherTransactions[voucher.voucherNumber].status === 'complete' ? <CheckCircleIcon /> :
                                  voucherTransactions[voucher.voucherNumber].status === 'partial' ? <WarningIcon /> :
                                  <ErrorIcon />
                                }
                                label={
                                  voucherTransactions[voucher.voucherNumber].status === 'complete' ? 'مكتمل' :
                                  voucherTransactions[voucher.voucherNumber].status === 'partial' ? 'جزئي' : 'مفقود'
                                }
                                size="small"
                                color={
                                  voucherTransactions[voucher.voucherNumber].status === 'complete' ? 'success' :
                                  voucherTransactions[voucher.voucherNumber].status === 'partial' ? 'warning' : 'error'
                                }
                              />
                            </Tooltip>
                          ) : (
                            <Chip label="لا توجد بيانات" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleVoucherMenuOpen(e, voucher.id)}
                            aria-haspopup="true"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* التنقل بين الصفحات - جدول السندات */}
              {totalVouchers > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}>
                  <Pagination
                    count={Math.ceil(totalVouchers / itemsPerPage)}
                    page={voucherPage}
                    onChange={handleVoucherPageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{ direction: 'ltr' }}
                  />
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
      </Paper>
      
      {/* حوار تأكيد الحذف */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>
          تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من رغبتك في حذف قالب السند {selectedTemplate?.name} ؟
          </Typography>
          {selectedTemplate && selectedTemplate.vouchersCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              لا يمكن حذف هذا القالب لأنه مرتبط بـ {selectedTemplate.vouchersCount} سند.
              يجب حذف جميع السندات المرتبطة أولاً.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDeleteOpen(false)}
            color="inherit"
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleDeleteTemplate}
            color="error"
            variant="contained"
            disabled={loading || (selectedTemplate?.vouchersCount || 0) > 0}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* حوار عرض تفاصيل القالب */}
      <Dialog
        open={viewTemplateOpen}
        onClose={() => setViewTemplateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تفاصيل قالب السند: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTemplate && (
            <Box sx={{ p: 1 }}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        اسم القالب
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedTemplate.name}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        نوع السند
                      </Typography>
                      <Chip
                        label={getVoucherTypeText(selectedTemplate.voucherType)}
                        variant="outlined"
                        size="small"
                        color={
                          selectedTemplate.voucherType === 'receipt' ? 'success' :
                          selectedTemplate.voucherType === 'payment' ? 'error' : 'primary'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        الوصف
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.description || 'لا يوجد وصف'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        الحالة
                      </Typography>
                      <Chip
                        label={selectedTemplate.isActive ? 'مفعل' : 'معطل'}
                        variant="outlined"
                        size="small"
                        color={selectedTemplate.isActive ? 'success' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        يتطلب موافقة
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.requiresApproval ? 'نعم' : 'لا'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        تطبيق رسوم
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.hasFees ? 'نعم' : 'لا'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    الحسابات المدينة المتاحة
                  </Typography>
                  {selectedTemplate.debitAccounts.length === 0 ? (
                    <Typography color="text.secondary">
                      لم يتم تحديد حسابات مدينة
                    </Typography>
                  ) : (
                    <List dense>
                      {/* يجب استبدالها بأسماء الحسابات الفعلية */}
                      {selectedTemplate.debitAccounts.map((accountId, index) => (
                        <ListItem key={accountId} divider={index < selectedTemplate.debitAccounts.length - 1}>
                          <ListItemText primary={`حساب مدين ${index + 1}`} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    الحسابات الدائنة المتاحة
                  </Typography>
                  {selectedTemplate.creditAccounts.length === 0 ? (
                    <Typography color="text.secondary">
                      لم يتم تحديد حسابات دائنة
                    </Typography>
                  ) : (
                    <List dense>
                      {/* يجب استبدالها بأسماء الحسابات الفعلية */}
                      {selectedTemplate.creditAccounts.map((accountId, index) => (
                        <ListItem key={accountId} divider={index < selectedTemplate.creditAccounts.length - 1}>
                          <ListItemText primary={`حساب دائن ${index + 1}`} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>
                
                {selectedTemplate.hasFees && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      مجموعات الرسوم المتاحة
                    </Typography>
                    {selectedTemplate.feeGroups.length === 0 ? (
                      <Typography color="text.secondary">
                        لم يتم تحديد مجموعات رسوم
                      </Typography>
                    ) : (
                      <List dense>
                        {/* يجب استبدالها بأسماء مجموعات الرسوم الفعلية */}
                        {selectedTemplate.feeGroups.map((groupId, index) => (
                          <ListItem key={groupId} divider={index < selectedTemplate.feeGroups.length - 1}>
                            <ListItemText primary={`مجموعة رسوم ${index + 1}`} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setViewTemplateOpen(false)}
            color="inherit"
          >
            إغلاق
          </Button>
          <Button 
            onClick={() => {
              setViewTemplateOpen(false);
              if (selectedTemplate) {
                handleCreateVoucher(selectedTemplate.id);
              }
            }}
            color="success"
            variant="contained"
            startIcon={<ReceiptIcon />}
          >
            إنشاء سند
          </Button>
          <Button 
            onClick={() => {
              setViewTemplateOpen(false);
              if (selectedTemplate) {
                handleEditTemplate(selectedTemplate.id);
              }
            }}
            color="primary"
            variant="outlined"
            startIcon={<EditIcon />}
          >
            تعديل القالب
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* قائمة إجراءات السند */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleVoucherMenuClose}
      >
        {canViewVoucher && (
          <MenuItem onClick={() => selectedVoucherId && handleViewVoucher(selectedVoucherId)}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            عرض التفاصيل
          </MenuItem>
        )}
        {canEditVoucher && (
          <MenuItem onClick={() => selectedVoucherId && handleEditVoucher(selectedVoucherId)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            تعديل
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedVoucherId && handlePrintVoucher(selectedVoucherId)}>
          <PrintIcon fontSize="small" sx={{ mr: 1 }} />
          طباعة
        </MenuItem>
        {canCreateVoucher && (
          <MenuItem>
            <FileCopyIcon fontSize="small" sx={{ mr: 1 }} />
            نسخ
          </MenuItem>
        )}
        {canChangeVoucherStatus && (
          <MenuItem onClick={() => selectedVoucherId && handleToggleVoucherStatus(selectedVoucherId)}>
            <ArrowForwardIcon fontSize="small" sx={{ mr: 1 }} />
            تغيير الحالة
          </MenuItem>
        )}
        <Divider />
        {canDeleteVoucher && (
          <MenuItem
            onClick={() => {
              if (selectedVoucher) {
                setConfirmDeleteVoucherOpen(true);
                handleVoucherMenuClose();
              }
            }}
            sx={{ color: theme.palette.error.main }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            حذف السند
          </MenuItem>
        )}
      </Menu>
      
      {/* حوار تأكيد حذف السند */}
      <Dialog
        open={confirmDeleteVoucherOpen}
        onClose={() => setConfirmDeleteVoucherOpen(false)}
      >
        <DialogTitle>
          تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من رغبتك في حذف السند {selectedVoucher?.voucherNumber} ؟
          </Typography>
          {selectedVoucher?.isApproved && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              هذا السند معتمد وحذفه سيؤدي إلى عكس تأثيره على أرصدة الحسابات!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteVoucherOpen(false)}
            color="inherit"
          >
            إلغاء
          </Button>
          <Button
            onClick={() => selectedVoucher?.id && handleDeleteVoucher(selectedVoucher.id)}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecializedVouchersList;